import { inject, signal, WritableSignal } from '@angular/core';
import { Auth, signOut } from '@angular/fire/auth';
import { FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { DealerRep } from '../dealer-rep/shared/dealer-rep.model';
import { Dealer } from '../dealer/shared/dealer.model';
import { Employee } from '../employee/shared/employee.model';
import { Folder } from '../folder/folder.model';
import { SnackBar } from '../snack-bar/snack-bar.component';
import { BaseUser } from '../user/base-user.model';
import { BaseComponentState } from './state-interface';
import { StateManager } from './state-manager.class';

export class BaseComponent {

    protected auth: Auth = inject(Auth);
    protected router: Router = inject(Router);
    protected matSnackBar: MatSnackBar = inject(MatSnackBar);
    protected stateManager: StateManager = inject(StateManager);

    public toolTipRight: FormControl = new FormControl("above");

    protected userAccount: BaseUser;
    protected userType: string;
    protected userId: number = -1;
    protected userDealerId: number = -1;
    protected userDealerName: string | undefined = "";

    protected pageIndex = 0;
    protected pageSize = 10;

    protected refresh: boolean;
    protected overrideSnackBar: string | undefined;

    private _message: WritableSignal<string> = signal<string>("");

    public constructor() {
        this.userType = this.stateManager.getUserType();
        this.userAccount = this.stateManager.getUserAccount();

        if (this.userType == "employee") {
            this.userId = (<Employee>this.userAccount).id;
            this.userDealerId = (<Employee>this.userAccount).dealer_id;
            this.userDealerName = (<Employee>this.userAccount).dealer?.name;
        } else if (this.userType == "dealer") {
            this.userId = (<Dealer>this.userAccount).id;
            this.userDealerId = (<Dealer>this.userAccount).id;
            this.userDealerName = (<Dealer>this.userAccount).name;
        } else if (this.userType == "dealer-rep") {
            this.userId = (<DealerRep>this.userAccount).id;
            this.userDealerId = (<DealerRep>this.userAccount).dealer_id;
            this.userDealerName = (<DealerRep>this.userAccount).dealer?.name;
        } else {
            // Setting the Dealer ID of the Customer to zero for now. As of the
            // time of writing these comments, outside of inserting a new
            // Customer to the database, there are no Use Cases where we
            // need to go from Customer to Dealer.
            // 
            // The database structure supports a many-to-many relationship between
            // Customer and Dealer. However, the current data access implementations
            // and model objects only support a one-to-one relationship from Customer
            // to Dealer. If eventually we have Use Cases where we need to go **from
            // Customer** to Dealer, then we'll need to modify the data access and
            // model object implementation accordingly.  
            this.userDealerId = 0;
        }

        this.refresh = false;
        this.overrideSnackBar = undefined;

        const currentNav = this.router.getCurrentNavigation();

        if (currentNav != undefined) {
            const state = currentNav.extras.state;
            if (state != undefined) {
                this.refresh = state["refresh"] != undefined;
            }
            const queryParams = currentNav.extras.queryParams;
            if (queryParams != undefined) {
                this.overrideSnackBar = queryParams["overrideSnackBar"];
            }
        }
    }
    
    public get isProd(): boolean {
        return environment.production;
    }

    public get isNonProd(): boolean {
        return !this.isProd;
    }

    protected getState(): BaseComponentState {
        return {
            message: this.message,
            pageIndex: this.pageIndex,
            pageSize: this.pageSize
        }
    }

    protected restoreState(state: BaseComponentState) {
        this.message = state.message;
        this.pageIndex = state.pageIndex;
        this.pageSize = state.pageSize;
    }

    public receiveMessage(value: string) {
        this._message.set(value);
    }

    protected get vehicleFirmware(): Folder[] {
        const vehicles:Folder[] = [];
        const folders = this.stateManager.getFirmwareFolders();

        for (const folder of folders) {
            if (folder.folder_name == folder.name) {
                vehicles.push(folder);
            }
        }
        return vehicles;
    }

    protected set firmwareFolders(value: Folder[]) {
        this.stateManager.setFirmwareFolders(value);
    }

    protected get message(): string {
        return this._message();
    }

    protected set message(value: string | string[]) {
        // If a single string is passed, then assume the
        // message is an error message.
        //
        // If an array is passed, then assume the message
        // is an error message unless a value of "false" is
        // in the second array entry.
        //
        // Error messages are "system" errors, not "user"
        // errors.

        if (Array.isArray(value)) {
            if (value[1] != "false") {
                console.error(value[0]);
            }
            this._message.set(value[0]);
        } else {
            if (value.length > 0) {
                console.error(value);
            }
            this._message.set(value);
        }
    }

    protected roundup(value: number) {
        return Math.ceil(value);
    }

    /*
    protected registerDealerIdListener() {

        this.dealerIdCtl.valueChanges.subscribe(dealerId => {

            if (this._saveCustomerNames != undefined) {
                this._customerNames.set(this._saveCustomerNames);
                this._saveCustomerNames = undefined;
            }

            if (dealerId != undefined
                && dealerId != "") {
                let filteredNames: Customer[] = [];

                for (const customer of this.customerNames) {
                    if (customer.dealer_id == Number(dealerId)) {
                        filteredNames.push(customer);
                    }
                }

                this._saveCustomerNames = this.customerNames;
                this._customerNames.set(filteredNames);
            }
        })
    }
    */

    /*
    protected registerCustomerIdListener() {

        this.customerIdCtl.valueChanges.subscribe(customerId => {

            if (this._saveDealerNames != undefined) {
                //this._dealerNames.set(this._saveDealerNames);
                this._saveDealerNames = undefined;
            }

            if (customerId != undefined
                && customerId != "") {
                let dealerId = 0;
                for (const customer of this.customerNames) {
                    if (customer.id == Number(customerId)) {
                        dealerId = customer.dealer_id;
                        break;
                    }
                }

                let filteredNames: Dealer[] = [];

                if (dealerId > 0) {
                    for (const dealer of this.dealerNames) {
                        if (dealer.id == dealerId) {
                            filteredNames.push(dealer);
                        }
                    }
                }

                //this._saveDealerNames = this.dealerNames;
                //this._dealerNames.set(filteredNames);
            }
        })
    }
    */

    protected formatDate(date: string) {
        return new Date(date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
        // Other examples
        //return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        //return d.toLocaleDateString('de-DE', { month: "short", day: "numeric", year: "numeric" });
        //return d.toLocaleDateString('ar-EG', { month: "short", day: "numeric", year: "numeric" });
    }

    protected userHasRole(role: string): boolean {
        return this.userAccount.user_roles.includes(role);
    }
    
    protected userTypeEquals(userType: string): boolean {
        return this.userType == userType;
    }

    protected navigate(route: string): void;
    protected navigate(route: string, params?: string | string[]): void;
    protected navigate(route: string, params?: string | string[]) {
        if (route != "sign-out") {
            if (params == undefined) {
                this.router.navigate([`${route}`])
            } else {
                if (!Array.isArray(params)) {
                    this.router.navigate([`${route}`, params])
                // Admittedly the following is not the best implementation, yet
                // good enough for now. If we get a Use Case where we exceed
                // 4 parameters, then we can look at a better implementation.
                } else if (params.length > 3) {
                    this.router.navigate([`${route}`, params[0], params[1], params[2], params[3]])
                } else if (params.length > 2) {
                    this.router.navigate([`${route}`, params[0], params[1], params[2]])
                } else {
                    this.router.navigate([`${route}`, params[0], params[1]])
                }
            }
        } else {
            this.signOut();
        }
    }

    private async signOut() {
        try {
            await signOut(this.auth);
            this.stateManager.reset();
        } catch (error: any) {
            console.error(error);
            this.message = error.message();
        }
    }

    protected handlePageEvent(e: PageEvent): void {
        this.pageSize = e.pageSize;
        this.pageIndex = e.pageIndex;
    }

    protected showSnackBar(message: string): void;
    protected showSnackBar(message: string, duration: number): void;
    protected showSnackBar(message: string, duration?: number): void {
        let _duration = 5000;

        if (duration != undefined) {
            _duration = duration * 1000;
        }

        if (duration != -1) {
            //this.snackBar.open(message, "Dismiss", { duration: _duration });
            this.matSnackBar.openFromComponent(SnackBar, { data: message, duration: _duration });
        } else {
            this.matSnackBar.open(message);
        }
    }

    protected dismissSnackBar() {
        this.matSnackBar.dismiss();
    }

    protected restoreControl(formControl: FormControl, value: string) {
        formControl.setValue(value);

        if ((value != undefined)
            && (value != null)
            && (value != "")) {
            this.markControl(formControl, this.dirty);
        }
    }

    protected markControl(formControl: FormControl, func: Function) {
        func(formControl);
    }

    protected pristine(formControl: FormControl) {
        formControl.markAsPristine();
    }

    protected dirty(formControl: FormControl) {
        formControl.markAsDirty();
    }
}
